/**
 * Feb 2026 — "+ Add new vendor" quick-add for the Expenses Ledger's Party
 * dropdown. Unlike Category (a plain taxonomy string), the Party list is
 * sourced from the real Vendor Registry, so this creates an actual
 * registry row via POST /admin/vendors — just with the absolute minimum
 * (Company, Contact Name, Email) instead of the full onboarding form.
 *
 * "Send Portal Invite & NDA" defaults OFF: the vendor is created silently
 * as `status: "active"` (server default when send_invite=false) so it's
 * immediately usable everywhere for reporting, with no email/NDA sent.
 * Ticking it flips to the full onboarding flow (invite email + NDA), same
 * as adding a vendor from Vendor Registry normally.
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function QuickAddVendorDialog({ open, onOpenChange, onAdded }) {
  const EMPTY = { company: "", name: "", email: "", sendInvite: false };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.company.trim() || !form.name.trim() || !form.email.trim()) {
      toast.error("Company Name, Contact Name and Email are all required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/vendors", {
        company: form.company.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        send_invite: form.sendInvite,
      });
      toast.success(form.sendInvite ? `Invited "${form.company}" — NDA + portal invite sent` : `Added "${form.company}" to the Vendor Registry`);
      onAdded?.(data.company || data.name);
      setForm(EMPTY);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) setForm(EMPTY); } }}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="quick-add-vendor-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Plus size={16} /> Add vendor</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="label-overline">Company Name *</Label>
            <Input className="rounded-sm mt-1" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="quick-vendor-company" />
          </div>
          <div>
            <Label className="label-overline">Contact Name *</Label>
            <Input className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-vendor-name" />
          </div>
          <div>
            <Label className="label-overline">Email *</Label>
            <Input type="email" className="rounded-sm mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-vendor-email" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 pt-1 cursor-pointer" data-testid="quick-vendor-send-invite-label">
            <Checkbox
              checked={form.sendInvite}
              onCheckedChange={(v) => setForm({ ...form, sendInvite: !!v })}
              data-testid="quick-vendor-send-invite-checkbox"
            />
            Send Portal Invite &amp; NDA now
          </label>
          {!form.sendInvite && (
            <p className="text-[11px] text-slate-400">Leave unticked to just add them to the registry silently — no email is sent, and you can invite them properly later from Vendor Registry.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="quick-vendor-save">
            {saving ? "Adding…" : "Add vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
